import { useEffect, useState } from 'react'
import { Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { DeleteIcon, EditSolidIcon, MoreVerticalIcon, PlusIcon } from '@dhl-ui/icons'
import { useListReturnHubs } from '@dhl/mdp-api'
import { type GridColDef, type GridRenderCellParams, Pagination } from '@dhl/components'
import { Box, Button, Grid, IconButton, MenuItem, Paper } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { convertToReturnHub, type ReturnHub } from '~/modules/returnhubsccc/model'
import { AdminDataGrid, lowercaseTransform, SearchTextField } from '~/common'

export const ReturnHubPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data: returnhubItems = [], isFetching } = useListReturnHubs({
    query: { select: (items) => items.map(convertToReturnHub) },
  })

  const [returnhubs, setReturnHubs] = useState<ReturnHub[]>([])
  const [initialReturnHubs, setInitReturnhubs] = useState<ReturnHub[]>([])

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [selectedRow, setSelectedRow] = useState<GridRenderCellParams | null | undefined>()

  useEffect(() => {
    if (returnhubItems) {
      setReturnHubs(returnhubItems)
      setInitReturnhubs(returnhubItems)
    }
  }, [returnhubItems])

  const handleSearchChange = (search: string) => {
    setPage(0)
    setReturnHubs(
      search
        ? initialReturnHubs.filter(
            ({ address: { city }, name }) =>
              city.toLowerCase().indexOf(search) !== -1 || name.toLowerCase().indexOf(search) !== -1
          )
        : initialReturnHubs
    )
  }

  const columns: GridColDef<ReturnHub>[] = [
    {
      field: 'name',
      headerName: t('admin.returnHubPage.returnHubsOverview.name'),
      flex: 1,
      valueGetter: (value, row) => row.name,
    },
    {
      field: 'address',
      headerName: t('admin.returnHubPage.returnHubsOverview.address'),
      flex: 1,
      valueGetter: (value, row) => `${row.address.street} 
      ${row.address.houseNumber}, 
      ${row.address.postalCode}, 
      ${row.address.city}`,
    },
    {
      field: 'actions',
      headerName: t('admin.returnHubPage.returnHubsOverview.actions'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton aria-label="edit return hub" onClick={handleClick(params)}>
          <MoreVerticalIcon data-testid="edit-returnhub-details" data-test-id={params.row.id} />
        </IconButton>
      ),
    },
  ]

  const handleClick = (params: GridRenderCellParams) => (event: any) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedRow(params)
  }

  const handleEdit = (row: { id: string }) => {
    navigate(links.editReturnHubLink(row.id))
  }
  const handleDelete = (row: { id: string }) => {
    navigate(links.deleteReturnHubLink(row.id))
  }

  const handleRowClick = (row: ReturnHub) => {
    navigate(links.returnHubsDetailLink(row.id))
    setSelectedRow(null)
    setMenuAnchor(null)
  }

  const handleClose = () => {
    setMenuAnchor(null)
    setSelectedRow(null)
  }

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid>
          <SearchTextField onChange={handleSearchChange} transform={lowercaseTransform} />
        </Grid>
        <Grid>
          <Button
            color="primary"
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => navigate(links.addReturnHubLink())}
          >
            {t('admin.returnHubPage.addNewReturnHub')}
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={isFetching}
            autoHeight
            rows={returnhubs || []}
            columns={columns}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            columnHeaderHeight={48}
            hideFooter
          />
        </Paper>

        <Box mt={2}>
          <Pagination
            page={page}
            count={returnhubs.length}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={() => setPageSize}
          />
        </Box>
      </Box>
      <Menu anchorEl={menuAnchor} keepMounted open={Boolean(menuAnchor)} onClose={handleClose}>
        <Paper>
          <MenuItem onClick={() => handleEdit(selectedRow?.row)} icon={<EditSolidIcon />}>
            {t('admin.returnHubPage.editReturnHub')}
          </MenuItem>
          <MenuItem isDestructive onClick={() => handleDelete(selectedRow?.row)} icon={<DeleteIcon />}>
            {t('admin.returnHubPage.deleteReturnHub')}
          </MenuItem>
        </Paper>
      </Menu>
    </>
  )
}
